import { useEffect, useState } from 'react';
import { CheckCircle, KeyboardArrowLeft } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  FormGroup,
  Grid,
  Stack,
  TextField,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  FieldValues,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';

import Form from 'components/Form';
import { getCollegeList } from 'apis/admin';
import MuiAutocomplete from './components/MuiAutocomplete';

import { ISendMailProps, IVerifyMailProps } from './types';
import SignUpDialog from './SignUpDialog';

import { styled } from '@mui/styles';

interface ISignUpProps {
  isSubmitting: boolean;
  cooldown: number;
}

interface Props {
  register: UseFormRegister<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  sendMailProps: ISendMailProps;
  verifyMailProps: IVerifyMailProps;
  signUpProps: ISignUpProps;
}

function SignUpForm(props: Props) {
  const [isTerms, setIsTerms] = useState(false);
  const [isPersonal, setIsPersonal] = useState(false);
  const sxBtnStyles = { width: 150, pl: 0, pr: 0, fontSize: 14 };
  const [cooldownTime, setCooldownTime] = useState(0);
  const [isEmailButtonDisabled, setIsEmailButtonDisabled] = useState(false);
  const {
    register,
    setValue,
    getValues,
    sendMailProps,
    verifyMailProps,
    signUpProps,
  } = props;

  const VerificationIcon = () => (
    <CheckCircle
      fontSize="small"
      color="primary"
      sx={{
        display: verifyMailProps.isVerification ? 'block' : 'none',
      }}
    />
  );

  // 출생년도 옵션
  const rendering = () => {
    const result = [];
    for (let i = 1950; i < 2011; i++) {
      result.push(
        <StMenuItem key={i} value={i}>
          {i}
        </StMenuItem>
      );
    }
    return result;
  };

  // select option style
  const StMenuItem = styled(MenuItem)({
    height: 55,
  });

  // 비밀번호 에러 찾기
  const [passwordValue, setPasswordValue] = useState('');

  const passwordRegex = () => {
    let check = /^([a-z0-9_\.-]+)@([\da-z\.-]+)\.([a-z\.]{2,10})$/;

    return check.test(passwordValue);
  };

  const [passwordCheck, setPasswordCheck] = useState('');

  // 이름에 특수문자 넣지못하게 막는
  const [이름, set이름] = useState('');

  const nameRegex = () => {
    let check = /@/;
    return check.test(이름);
  };

  // 이메일 유효성
  const [이메일, set이메일] = useState('');

  const emailRegex = () => {
    let check = new RegExp(
      "([!#-'*+/-9=?A-Z^-~-]+(.[!#-'*+/-9=?A-Z^-~-]+)*|\"([]!#-[^-~ \t]|(\\[\t -~]))+\")@([!#-'*+/-9=?A-Z^-~-]+(.[!#-'*+/-9=?A-Z^-~-]+)*|[[\t -Z^-~]*])"
    );
    return check.test(이메일);
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (cooldownTime > 0) {
      setIsEmailButtonDisabled(true);
      timer = setInterval(() => {
        setCooldownTime(prev => prev - 1);
      }, 1000);
    } else {
      setIsEmailButtonDisabled(false);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [cooldownTime]);

  const handleEmailSend = () => {
    sendMailProps.onClick();
    setCooldownTime(60);
  };

  const getEmailHelperText = () => {
    if (!emailRegex()) {
      return 'Их сургуулийн и-мэйлээ оруулна уу (хэрэв байхгүй бол хувийн и-мэйлээ оруулна уу)';
    }
    if (cooldownTime > 0 && sendMailProps.isSendMail) {
      return 'Мөнхгал: Баталгаажуулалтын кодыг 1 минутын дараа дахин илгээж болно.';
    }
    return '';
  };

  return (
    <Box>
      <Container maxWidth="sm" sx={{ mt: 7.5, mb: 6 }}>
        <Button
          href="/#/"
          size="large"
          color="inherit"
          startIcon={<KeyboardArrowLeft />}
          sx={{ mb: 5, p: 0 }}
        >
          Бүртгэл үүсгэх
        </Button>

        <Stack spacing={2.5}>
          <TextField
            // required
            fullWidth
            type="email"
            sx={{ display: 'none' }}
            {...register('userEmail')}
          />
          <Form.Item label="И-мэйл">
            <Box display="flex" gap={1}>
              <TextField
                helperText={getEmailHelperText()}
                color={emailRegex() ? 'success' : 'warning'}
                required
                fullWidth
                type="email"
                {...register('userEmail', {
                  onChange: e => set이메일(e.target.value),
                })}
              />
              <LoadingButton
                sx={{ ...sxBtnStyles, maxHeight: 56, fontSize: 12 }}
                loading={sendMailProps.isLoading}
                variant={sendMailProps.isSendMail ? 'outlined' : 'contained'}
                onClick={handleEmailSend}
                disabled={isEmailButtonDisabled}
              >
                {sendMailProps.isSendMail
                  ? cooldownTime > 0
                    ? `${cooldownTime}секунд`
                    : 'Дахин илгээх'
                  : 'БАТАЛГААЖУУЛАХ'}
              </LoadingButton>
            </Box>
          </Form.Item>
          <Form.Item
            label="Баталгаажуулах дугаар"
            isHidden={!sendMailProps.isSendMail}
          >
            <Box display="flex" gap={1}>
              <TextField
                // required
                fullWidth
                placeholder="Их сургуулийн и-мэйлээ оруулна уу"
                InputProps={{
                  readOnly: verifyMailProps.isVerification,
                  endAdornment: <VerificationIcon />,
                }}
                {...register('userCode')}
              />
              <LoadingButton
                variant="contained"
                loading={verifyMailProps.isLoading}
                onClick={verifyMailProps.onClick}
                sx={{
                  ...sxBtnStyles,
                  display: !verifyMailProps.isVerification ? 'block' : 'none',
                  height: '55px',
                }}
              >
                Оруулах
              </LoadingButton>
            </Box>
            <Typography
              sx={{
                fontSize: '12px',
                color: 'rgba(0, 0, 0, 0.6) ',
                marginLeft: '14px',
                marginTop: '5px',
              }}
            >
              Хэрэв та баталгаажуулах дугаарыг хүлээн аваагүй бол спам хавтсыг
              шалгана уу.
            </Typography>
          </Form.Item>
          <input
            type="password"
            style={{ display: 'none' }}
            aria-hidden="true"
          />

          <Form.Item label="Нууц үг">
            <Form.Password
              required
              value={passwordValue}
              fullWidth
              placeholder="8 тэмдэгт, Англи үсэг + тоо + тусгай тэмдэгт"
              helperText={
                passwordRegex()
                  ? "It's safe!"
                  : 'Тусгай тэмдэгтүүдэд зөвшөөрөгдсөн: @ $ ! % * # ^ () _ = | ]'
              }
              color={passwordRegex() ? 'success' : 'warning'}
              {...register('userPassword', {
                onChange: e => setPasswordValue(e.target.value),
              })}
            />
          </Form.Item>
          <Form.Item label="Нууц үгээ давтана уу">
            <Form.Password
              required
              fullWidth
              isHideVisibleBtn
              helperText={
                passwordValue === passwordCheck
                  ? 'Таарч байна!'
                  : 'Таарахгүй байна!'
              }
              color={passwordValue === passwordCheck ? 'success' : 'warning'}
              {...register('userPasswordConfirm', {
                onChange: e => setPasswordCheck(e.target.value),
              })}
            />
          </Form.Item>
          <TextField
            helperText={
              nameRegex()
                ? 'Please be careful not to write your email address.'
                : ''
            }
            color={nameRegex() ? 'warning' : 'success'}
            required
            sx={{ display: 'none' }}
            fullWidth
            {...register('userName', {
              onChange: e => set이름(e.target.value),
            })}
          />
          <Form.Item label="Нэр">
            <TextField
              helperText={
                nameRegex()
                  ? 'Please be careful not to write your email address.'
                  : ''
              }
              color={nameRegex() ? 'warning' : 'success'}
              required
              fullWidth
              {...register('userName', {
                onChange: e => set이름(e.target.value),
              })}
            />
          </Form.Item>
          <Grid container>
            <Grid item xs={6}>
              <Form.Item label="Хүйс">
                <Form.MuiRadioGroup
                  i18nKey="GENDER"
                  values={[1, 2]}
                  defaultValue={getValues('gender')}
                  onChange={value => setValue('gender', value)}
                />
              </Form.Item>
            </Grid>
            <Grid item xs={6}>
              <Form.Item label="Албан тушаал">
                <Form.MuiRadioGroup
                  i18nKey="GRADE"
                  values={[1, 2]}
                  defaultValue={getValues('grade')}
                  onChange={value => setValue('grade', value)}
                />
              </Form.Item>
            </Grid>
          </Grid>

          <Form.Item label="Сургууль (Их сургууль/Коллеж)">
            <MuiAutocomplete
              listKey="college_lists"
              valueKey="college_id"
              noOptionsText="Please enter another school"
              onChange={value => setValue('college', value.college_id)}
              getApi={getCollegeList}
              getOptionLabel={option => option.college_name}
            />
          </Form.Item>
          <Form.Item label="Оюутны дугаар (Профессорын дугаар)">
            <TextField
              // required
              fullWidth
              type="text"
              variant="outlined"
              {...register('studentNo')}
            />
          </Form.Item>
          <Form.Item label="Төрсөн он (*сонголтот оруулах)">
            <TextField
              // required
              fullWidth
              select
              InputLabelProps={{ shrink: true }}
              defaultValue="2010"
              {...register('birth')}
            >
              {rendering()}
            </TextField>
          </Form.Item>
        </Stack>

        <FormGroup sx={{ mt: 2.5 }}>
          <FormControlLabel
            label="(Заавал) Нөхцөл, болзлыг зөвшөөрө"
            control={
              <Checkbox
                // required
                size="small"
                onChange={(_, checked) => setIsTerms(checked)}
              />
            }
          />
          <FormControlLabel
            label="(Заавал) Хувийн мэдээлэл цуглуулахыг зөвшөөрөх"
            control={
              <Checkbox
                // required
                size="small"
                onChange={(_, checked) => setIsPersonal(checked)}
              />
            }
          />
        </FormGroup>

        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={signUpProps.isSubmitting}
          disabled={signUpProps.cooldown > 0}
          sx={{ mt: 3.25 }}
        >
          {signUpProps.cooldown > 0
            ? `БҮРТГҮҮЛЭХ (${signUpProps.cooldown}секунд)`
            : 'БҮРТГҮҮЛЭХ'}
        </LoadingButton>
      </Container>

      <SignUpDialog.TermsOfService
        isOpen={isTerms}
        onClose={() => setIsTerms(false)}
      />

      <SignUpDialog.PersonalInfo
        isOpen={isPersonal}
        onClose={() => setIsPersonal(false)}
      />
    </Box>
  );
}

export default SignUpForm;
